import request from 'services/axios'

/**
 * How the loan-create endpoint wants a «مبلغ ثابت» کارمزد.
 *
 * `draft-installments` and `validate-draft-installments` take the fee as
 * `interest_type` + `fixed_interest_amount`; `POST …/loans/` does not read
 * either — it knows a fixed fee only as `is_not_interest_rate` +
 * `increase_amount` (`LoanSerializer.save`). All three create calls used to
 * hard-code those to `false` / `0`, so a fixed fee was DROPPED at the last
 * step: with «افزودن به هر قسط» the instalments that included it failed the
 * server's sum check and the loan was silently re-split without the fee;
 * with «کسر از مبلغ وام» nothing was deducted from the payout.
 *
 * Anything that is not `percentage` is a fixed fee — the same reading
 * `loanInterestFields` uses when it zeroes `interest_rate`.
 */
export const fixedFeeFields = (payload: {
    interest_type?: string
    fixed_interest_amount?: number | string
}) => {
    const fixed = Number(payload.fixed_interest_amount || 0) || 0
    const isFixed = payload.interest_type !== 'percentage' && fixed > 0
    return {
        is_not_interest_rate: isFixed,
        increase_amount: isFixed ? fixed : 0,
    }
}

export const ManagementAddNewLoan = async (payload: IManagementBodyFirst) => {
    const { data }: { data: IManagementAddNewLoans } = await request.post(
        `/api/v1/moneypools/${payload.moneyPool}/draft-installments/`,
        {
            amount: Number(payload.amount),
            interest_pay_mode: payload.interest_pay_mode,
            interest_rate: Number(payload.interest_rate),
            interest_type: payload.interest_type,
            fixed_interest_amount: payload.fixed_interest_amount,
            number_of_repayments: Number(payload.number_of_repayments),
            pay_date: payload.pay_date,
            due_date: payload.due_date,
            number_of_repaid_installments: 0,
            repayment_interval: 'mnt',
        }
    )
    return data
}

export const ManagementAddNewLoanValidate = async (
    payload: IManagementBodyValidate
) => {
    const { data } = await request.post(
        `/api/v1/moneypools/validate-draft-installments/`,
        {
            total_amount: Number(payload.amount),
            interest_pay_mode: payload.interest_pay_mode,
            interest_rate: Number(payload.interest_rate),
            interest_type: payload.interest_type,
            fixed_interest_amount: payload.fixed_interest_amount,
            installments: payload.installments,
        }
    )
    return data
}

export const ManagementAddNewLoanValidateJustAdd = async (
    payload: IManagementBodyAddJust
) => {
    const { data } = await request.post(
        `/api/v1/payment/moneypools/${payload.moneyPool}/loans/`,
        {
            number_of_repayments: Number(payload.number_of_repayments),
            repayment_interval: 'mnt',
            number_of_repaid_installments: 0,
            interest_rate: Number(payload.interest_rate),
            ...fixedFeeFields(payload),
            interest_pay_mode: payload.interest_pay_mode,
            pay_date: payload.pay_date,
            due_date: payload.due_date,
            cashout: {
                poolship: Number(payload.poolShip),
                amount: Number(payload.amount),
                type: 'ctl',
                tag: payload.tag,
            },
            installment_data: payload.installments,
            loan_demand: payload?.loan_demand,
            guarantor_ids: payload.guarantor_ids ?? [],
        }
    )
    return data
}

export const ManagementAddNewLoanValidateFuckingAdd = async (
    payload: IManagementAddPayload
) => {
    const { data } = await request.post(
        `/api/v1/payment/moneypools/${payload.moneyPool}/loans/`,
        {
            number_of_repayments: Number(payload.number_of_repayments),
            repayment_interval: 'mnt',
            interest_rate: Number(payload.interest_rate),
            ...fixedFeeFields(payload),
            interest_pay_mode: payload.interest_pay_mode,
            pay_date: payload.pay_date,
            due_date: payload.due_date,
            cashout: {
                poolship: Number(payload.poolShip),
                amount: Number(payload.amount),
                type: 'ctl',
                tag: payload.tag,
                transaction: {
                    bank_account: payload.bankAccount,
                    ctx_id: payload.ctx_id,
                    ctx_type: payload.ctx_type,
                    destination: payload.destination,
                    is_group_pay: false,
                    payer_name: '',
                    receiver: payload.receiver,
                    receiver_name: '',
                    source: 'hbb',
                    state: '',
                    state_time: '',
                    transaction_code: '',
                },
            },
            installment_data: payload.installments,
            loan_demand: payload?.loan_demand,
            guarantor_ids: payload.guarantor_ids ?? [],
        }
    )
    return data
}

export const ManagementAddNewLoanValidateAddOld = async (
    payload: IIManagementAddOldPayload
) => {
    const { data } = await request.post(
        `/api/v1/payment/moneypools/${payload.moneyPool}/loans/`,
        {
            number_of_repayments: Number(payload.number_of_repayments),
            repayment_interval: 'mnt',
            interest_rate: Number(payload.interest_rate),
            ...fixedFeeFields(payload),
            interest_pay_mode: payload.interest_pay_mode,
            pay_date: payload.pay_date,
            due_date: payload.due_date,
            number_of_repaid_installments:
                payload.number_of_repaid_installments,
            cashout: {
                poolship: Number(payload.poolShip),
                amount: Number(payload.amount),
                type: 'ctl',
                tag: payload.tag,
            },
            installment_data: payload.installments,
            guarantor_ids: payload.guarantor_ids ?? [],
        }
    )
    return data
}
